import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Toggle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/components/ui';
import { cn } from '@/shared/utils';

interface TabItem {
  value: string;
  label: string;
  icon?: LucideIcon;
  disabled?: boolean;
  tooltip?: string;
}

interface TabNavProps {
  tabs: TabItem[];
  basePath: string;
  rightContent?: ReactNode;
  /** Visual style variant */
  variant?: 'pills' | 'underline';
}

export const TabNavPortal = ({ children }: { children: ReactNode }) => {
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const container = document.getElementById('tab-nav-portal-container');
    if (container) {
      setPortalContainer(container);
    }
  }, []);

  if (!portalContainer) return null;

  return createPortal(children, portalContainer);
};

export function TabNav({ tabs, basePath, rightContent, variant = 'pills' }: TabNavProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentPath, setCurrentPath] = useState<string | undefined>(undefined);

  useEffect(() => {
    const path = location.pathname.split('/').pop();
    setCurrentPath(path);
  }, [location]);

  const isActive = (value: string) => currentPath === value;

  const handleNavigate = (value: string) => {
    navigate(`${basePath}/${value}${location.search}`);
  };

  if (variant === 'underline') {
    return (
      <div className="border-b border-border mb-8 flex items-end justify-between gap-4">
        <nav className="flex flex-wrap gap-8">
          <TooltipProvider>
            {tabs.map((tab) => {
              const active = isActive(tab.value);
              const Icon = tab.icon;
              const button = (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => !tab.disabled && handleNavigate(tab.value)}
                  className={cn(
                    'pb-4 text-sm font-medium border-b-2 transition-colors inline-flex items-center gap-2',
                    active
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border',
                    tab.disabled && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  {Icon && <Icon size={16} />}
                  {tab.label}
                </button>
              );

              if (tab.tooltip && tab.disabled) {
                return (
                  <Tooltip key={tab.value}>
                    <TooltipTrigger asChild>{button}</TooltipTrigger>
                    <TooltipContent>
                      <p>{tab.tooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return button;
            })}
          </TooltipProvider>
        </nav>
        <div id="tab-nav-portal-container" className="ml-auto flex items-center pb-3">
          <TabNavPortal>{rightContent}</TabNavPortal>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between mb-6">
      <div className="flex flex-wrap gap-2">
        <TooltipProvider>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const toggle = (
              <Toggle
                key={tab.value}
                pressed={isActive(tab.value)}
                onPressedChange={() => !tab.disabled && handleNavigate(tab.value)}
                className={tab.disabled ? 'opacity-50 cursor-not-allowed' : ''}
              >
                <span className="inline-flex items-center gap-2">
                  {Icon && <Icon size={14} />}
                  {tab.label}
                </span>
              </Toggle>
            );

            if (tab.tooltip && tab.disabled) {
              return (
                <Tooltip key={tab.value}>
                  <TooltipTrigger asChild>{toggle}</TooltipTrigger>
                  <TooltipContent>
                    <p>{tab.tooltip}</p>
                  </TooltipContent>
                </Tooltip>
              );
            }

            return toggle;
          })}
        </TooltipProvider>
      </div>
      <div id="tab-nav-portal-container" className="ml-auto flex items-center">
        <TabNavPortal>{rightContent}</TabNavPortal>
      </div>
    </div>
  );
}
